import {
  API_ERROR_CODES,
  type APIErrorCode,
  type APIErrorShape,
  type ZodIssue,
} from "@repo/utils/lib/errors";
import { TRPCClientError } from "@trpc/client";
import { type TranslationValues, useTranslations } from "next-intl";
import type { UseFormReturn } from "react-hook-form";
import { toast } from "sonner";

type ErrorOpts = {
  defaultError?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== "object" || value === null) return false;

  for (const key in value) {
    if (!Object.hasOwn(value, key)) return false;
  }

  return true;
};

const getKeyAndValues = (
  param: unknown,
  defaultKey: string,
): {
  values: Record<string, unknown>;
  key: string;
} => {
  if (typeof param === "string") return { key: param, values: {} };

  if (isRecord(param)) {
    const key =
      "key" in param && typeof param.key === "string" ? param.key : defaultKey;
    const values =
      "values" in param && isRecord(param.values) ? param.values : {};
    return { key, values };
  }

  return { key: defaultKey, values: {} };
};

export function useErrorFormatter() {
  const t = useTranslations();

  function localizeError(issue: ZodIssue): string {
    if (issue.code === "custom") {
      const { key, values } = getKeyAndValues(
        issue.params?.i18n,
        "zod.errors.custom",
      );

      return t(
        `zod.errors.custom.${key}` as Parameters<typeof t>[0],
        (values as TranslationValues) ?? {},
      );
    }

    return issue.message;
  }

  function setApiErrorsToForm<Form extends UseFormReturn<any, any>>(
    err: unknown,
    form: Form,
    { defaultError = t("errors.generic") }: ErrorOpts = {},
  ) {
    if (err instanceof TRPCClientError && err.shape) {
      const shape = err.shape as APIErrorShape;

      if (shape.data?.zodError?.fieldErrors) {
        Object.entries(shape.data.zodError.fieldErrors ?? {}).forEach(
          ([field, errors]) => {
            if (errors.length > 0) {
              const error = errors[0];
              form.setError(field, {
                message: localizeError(error),
              });
            }
          },
        );
      } else if (shape.data?.zodError?.formErrors) {
        if (shape.data.zodError.formErrors.length > 0) {
          const error = shape.data.zodError.formErrors[0];
          form.setError("root", {
            message: localizeError(error),
          });
        }
      } else if (
        shape.message &&
        API_ERROR_CODES.includes(shape.message as any)
      ) {
        form.setError("root", {
          message: t(`errors.trpc.${shape.message as APIErrorCode}`),
        });
      } else if (shape.message) {
        form.setError("root", {
          message: shape.message,
        });
      } else {
        form.setError("root", {
          message: defaultError,
        });
      }
    } else {
      form.setError("root", {
        message: defaultError,
      });
    }
  }

  function formatError(
    err: unknown,
    { defaultError = t("errors.generic") }: ErrorOpts = {},
  ): string {
    if (err instanceof TRPCClientError && err.shape) {
      const shape = err.shape as APIErrorShape;

      if (shape.data?.zodError?.fieldErrors) {
        return Object.values(shape.data.zodError.fieldErrors)
          .flat()
          .map(localizeError)
          .join(". ");
      }

      if (shape.data?.zodError?.formErrors) {
        if (shape.data.zodError.formErrors.length > 0) {
          return shape.data.zodError.formErrors.map(localizeError).join(". ");
        }
      } else if (
        shape.message &&
        API_ERROR_CODES.includes(shape.message as any)
      ) {
        return t(`errors.trpc.${shape.message as APIErrorCode}`);
      } else if (shape.message) {
        return shape.message;
      }
    }

    return defaultError;
  }

  function displayErrorToast(
    err: unknown,
    { defaultError = t("errors.generic") }: ErrorOpts = {},
  ): void {
    const errorMessage = formatError(err, { defaultError });
    toast.error(errorMessage);
  }

  return {
    setApiErrorsToForm,
    formatError,
    displayErrorToast,
  };
}
